import 'react-date-range/dist/styles.css'; // main style file
import 'react-date-range/dist/theme/default.css'; // theme css file
import { Row, Col } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';
import React, { useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react';

import {
  addDays,
  differenceInCalendarDays,
  eachDayOfInterval,
  endOfDay,
  endOfMonth,
  format,
  getDay,
  isSameDay,
  isWeekend,
  startOfDay,
  startOfMonth,
  sub,
  subDays,
  subMonths,
  subWeeks,
  startOfWeek,
  endOfWeek,
  subYears,
  startOfYear,
  endOfYear,
  add,
  addMonths,
  isThisMonth,
  isThisYear,
  differenceInDays
} from 'date-fns';
import Form from 'react-bootstrap/Form';
import { DateRangePicker } from 'react-date-range';
import { Popover, Whisper } from 'rsuite';
import Calender from 'src/assets/images/common/calender.svg';
import collapseIcon from 'src/assets/images/common/down-arrow.svg';
import collapseLight from 'src/assets/images/common/down-arrow-light.svg';
import TickIcon from 'src/assets/images/common/white-tick.svg';
import { useCommonContext } from 'src/contexts/CommonContext.js';
import { ThemeContext } from 'src/providers/themeProvider';
import Input from 'src/components/Form/Input.js';

import { SQP_CALENDAR_OPTIONS } from './report-targeting.constant.js';
import { usePayloadContext } from 'src/contexts/PayloadContext.js';
import { useSmartSearchContext } from 'src/contexts/SmartSearchContext.js';
import { useDayPartingContext } from 'src/contexts/DayPartingContext.js';
import isAfter from 'date-fns/isAfter';
import { getTimeZone, getTimeZoneAbrv, zoneToData } from 'src/config/timezone.js';
import moment from 'moment-timezone';
import CheckBox from 'src/components/Form/checkbox.js';
import DownChevron, { UpChevronIcon } from 'src/assets/icons/DownChevron.jsx';
import { isEqual } from 'lodash';
import { isDate } from 'lodash';
import { useAmzDspContext } from 'src/contexts/AmzDspContext.js';
import useApi from 'src/hooks/useApi';
import services from 'src/api/service';
import { dateFormatPayload } from './date.constant.js';
import CustomTooltip from 'src/components/Popover/CustomTooltip.js';
import { enGB } from 'date-fns/locale';
import { SkeletonLoader, SkeletonComponents } from 'src/utils/skeletonLoader';

// Export commonly used skeleton components
export const CalendarSkeletonLoader = SkeletonComponents.CalendarLoader;
export const DatePickerSkeletonLoader = SkeletonComponents.DatePickerLoader;

const CollapseArrowIcon = () => {
  const [theme] = useContext(ThemeContext);
  return <img src={theme.theme === 'DARK' ? collapseLight : collapseIcon} alt="date_picker" className="wid-10 ml-3" />;
};

function dateFormat(dateString) {
  const date = new Date(dateString);
  return date;
}

export default function Calendar({
  availableColumns = [],
  setAvailableColumns,
  crossFilter = [],
  setCrossFilter,
  reportTitle = '',
  onEmitDateRange,
  classes = '',
  customclass = '',
  children,
  preDateSelected,
  isCustomCalender = 0,
  isCalendarOnly = 0,
  isSnapshot = window.location.href.indexOf('amazon/targeting') > -1 || 
  window.location.href.indexOf('summary/blinkit') > -1 || 
  window.location.href.indexOf('summary/myntra') > -1 ||
  window.location.href.indexOf('summary/flipkart') > -1 ||
  window.location.href.indexOf('amazon-dsp') > -1 ||
    window.location.href.indexOf('sov') > -1 ||
    window.location.href.indexOf('master-overview') > -1 ||
    window.location.href.indexOf('snapshot') > -1 ||
    window.location.href.indexOf('amazon-dayparting') > -1 ||
    window.location.href.indexOf('marketing-stream') > -1 ||
    window.location.href.indexOf('/instamart') > -1 ||
    window.location.href.indexOf('search-query-performance') > -1,
  compare = 0,
  defaultCalendar = 0,
  isDirectCalendar = false,
  isDirectType = '',
  masterSearchCalendar = 0,
  calendarClasses,
  fromTable = 0,
  placement,
  customOptions = {},
  showCompareInfo= true,
  customSelectHidden = false,
  maxDate = undefined
}) {
  const { commonState, commonDispatch } = useCommonContext();
  const { amzDspState } = useAmzDspContext();
  const [crossFilterPayload, setCrossFilterPayload] = useState(crossFilter);
  const getDefaultInstate = useMemo(() => {
    return preDateSelected?.hourFilter && preDateSelected?.hourFilter?.length > 0 ? preDateSelected?.hourFilter : hourFilterList
  }, [preDateSelected]); 
  const dateRangePickerRef = useRef();
  // Helpers to force Sun-Sat weeks and prevent selecting beyond last complete week
  const getLastCompleteSaturday = useCallback(() => {
    // Check if we're on SQP page
    const isSQPPage = window.location.href.indexOf('amazon/search-query-performance') > -1;
    
    // Get profile timezone from localStorage (account timezone, not system timezone)
    const profileTimezone = getTimeZone();
    
    // Calculate last complete Saturday in profile timezone
    // Get current date/time in profile timezone
    const nowInProfileTz = moment().tz(profileTimezone);
    const currentDayOfWeek = nowInProfileTz.day(); // 0 = Sunday, ..., 6 = Saturday
    
    let lastCompleteSaturdayInProfileTz;
    if (currentDayOfWeek === 6) {
      // If today is Saturday, the last complete Saturday is 7 days ago
      lastCompleteSaturdayInProfileTz = nowInProfileTz.clone().subtract(7, 'days');
    } else {
      // For any other day, find the most recent Saturday
      lastCompleteSaturdayInProfileTz = nowInProfileTz.clone().subtract(currentDayOfWeek + 1, 'days');
    }
    
    // Ensure it's the end of week (Saturday) in profile timezone
    // Get the date components from the moment object to create a Date object
    // This ensures we're working with the correct date in profile timezone
    const saturdayDate = lastCompleteSaturdayInProfileTz.clone().day(6); // Set to Saturday of that week
    const saturdayDateString = saturdayDate.format('YYYY-MM-DD');
    const [year, month, day] = saturdayDateString.split('-').map(Number);
    const lastCompleteSaturday = new Date(year, month - 1, day); // month is 0-indexed
    
    // Use sqp_end_date from CommonContext if available and on SQP page
    // Always use the minimum of: database end date OR last complete Saturday in profile timezone
    // This ensures users see data only till their account's timezone's last complete week
    if (isSQPPage && commonState?.dateInfo?.sqp_end_date) {
      const sqpEndDate = new Date(commonState.dateInfo.sqp_end_date);
      if (sqpEndDate instanceof Date && !isNaN(sqpEndDate.getTime())) {
        // Convert database end date to profile timezone and ensure it's the end of week (Saturday)
        const dateInProfileTz = moment(sqpEndDate).tz(profileTimezone);
        // Get Saturday of that week in profile timezone
        const saturdayInProfileTz = dateInProfileTz.clone().day(6);
        const saturdayDateString = saturdayInProfileTz.format('YYYY-MM-DD');
        const [year, month, day] = saturdayDateString.split('-').map(Number);
        const dbEndDateNormalized = new Date(year, month - 1, day); // month is 0-indexed
        
        // Use the minimum of database date or profile's last complete Saturday
        return dbEndDateNormalized < lastCompleteSaturday ? dbEndDateNormalized : lastCompleteSaturday;
      }
    }
    
    // Fallback: Return calculated last complete Saturday in profile timezone
    return lastCompleteSaturday;
  }, [commonState?.dateInfo?.sqp_end_date]);

  const snapToIsoWeeks = useCallback((startDate, endDate) => {
    const lastSaturday = getLastCompleteSaturday();
    let weekStart = startOfWeek(new Date(startDate), { weekStartsOn: 0 });
    let weekEnd = endOfWeek(new Date(endDate), { weekStartsOn: 0 });

    // Do not allow future/incomplete week
    if (weekEnd > lastSaturday) {
      weekEnd = new Date(lastSaturday);
    }
    // If selection falls in current incomplete week, snap to last complete week
    const endOfSelWeek = endOfWeek(new Date(startDate), { weekStartsOn: 0 });
    if (endOfSelWeek > lastSaturday) {
      weekEnd = new Date(lastSaturday);
      weekStart = startOfWeek(weekEnd, { weekStartsOn: 0 });
    }
    if (weekStart > weekEnd) {
      weekStart = startOfWeek(weekEnd, { weekStartsOn: 0 });
    }
    return { weekStart, weekEnd };
  }, [getLastCompleteSaturday]);

  // Helper to calculate comparison period based on selected duration
  const calculateComparisonPeriod = useCallback((startDate, endDate) => {
    const duration = differenceInDays(new Date(endDate), new Date(startDate)) + 1;
    const pastEndDate = subDays(new Date(startDate), 1);
    const pastStartDate = subDays(pastEndDate, duration - 1);
    return { pastStartDate, pastEndDate };
  }, []);

  const isAudienceCreation = window.location.href.indexOf('amazon-create-audience') > -1
  const [audienceCustomDays, setAudienceCustomDays] = useState(0);
  const [customWeeks, setCustomWeeks] = useState(5);

  const [tempDateRange, setTempDateRange] = useState(
    preDateSelected && Object.keys(preDateSelected).length > 0
      ? {
        ...preDateSelected,
        dates: [],
        compareDates: [],
        hourFilter: getDefaultInstate
      }
      : {
        startDate: new Date(zoneToData(subDays(new Date(), 30))),
        endDate: new Date(zoneToData(subDays(new Date(), 1))),
        pastStartDate: new Date(zoneToData(subDays(new Date(), 60))),
        pastEndDate: new Date(zoneToData(subDays(new Date(), 31))),
        dates: [],
        compareDates: [],
        key: 'Last 30 Days',
        hourFilter: getDefaultInstate
      }
  );

  const [hoursList, setHoursList] = useState(getDefaultInstate);

  useEffect(() => {
    setHoursList(getDefaultInstate);
  }, [getDefaultInstate]);

  // Initialize component safely
  useEffect(() => {
    // Set initialized to true after a short delay to ensure all contexts are ready
    const timer = setTimeout(() => {
      setIsInitialized(true);
    }, 1000);
    
    return () => clearTimeout(timer);
  }, []);

  const tillToday =
    window.location.href.indexOf('user-activity') > -1 ||
    window.location.href.indexOf('amazon-dayparting') > -1 ||
    // window.location.href.indexOf('summary/flipkart') > -1 ||
    // window.location.href.indexOf('flipkart-snapshot') > -1 ||
    // window.location.href.indexOf('flipkart-master-overview') > -1 ||
    // window.location.href.indexOf('summary/myntra') > -1 ||
    window.location.href.indexOf('amazon-snapshot') > -1 ||
    window.location.href.indexOf('amazon-master-overview') > -1 ||
    window.location.href.indexOf('amazon-automated-rules') > -1 ||
    window.location.href.indexOf('amazon-dsp-automated-rules') > -1 ||
    window.location.href.indexOf('amazon/marketing-stream') > -1 ||
    window.location.href.indexOf('dsp-invoice') > -1 ||
    window.location.href.indexOf('billing-payments') > -1 ||
    window.location.href.indexOf('amazon-dsp-logs') > -1 ||
    window.location.href.indexOf('amazon-dsp/marketing-stream') > -1 ||
    window.location.href.indexOf('smart-campaigns') > -1 || 
    window.location.href.indexOf('hector-registration') > -1 || 
    window.location.href.indexOf('timespend') > -1 ||
    window.location.href.indexOf('amazon-dsp-dayparting-performance') > -1 ||
    window.location.href.indexOf('flipkart-automated-rules') > -1 ||
    window.location.href.indexOf('cpc-job-listing') > -1 ||
    window.location.href.indexOf('master-dayparting-tracking') > -1;


  const isAmazonMarketingStream = window.location.href.indexOf('amazon/marketing-stream') > -1 || window.location.href.indexOf('amazon-dsp/marketing-stream') > -1
  const isAmazonSQP = window.location.href.indexOf('amazon/search-query-performance') > -1

  const [showDatepicker, setShowdatepicker] = useState(false);
  const [dateRanges, setDateRanges] = useState([]);
  const [doCompare, setDoCompare] = useState(
    showCompareInfo && preDateSelected && Object.keys(preDateSelected).length > 0 && 'compare' in preDateSelected ? preDateSelected?.compare : false
  );
  const [compareSelected, doCompareSelected] = useState(
    showCompareInfo && preDateSelected && Object.keys(preDateSelected).length > 0 && 'compare' in preDateSelected ? preDateSelected?.compare : false
  );
  const [startFailureMessage, setStartFailureMessage] = useState('');
  const [endFailureMessage, setEndFailureMessage] = useState('');
  const [dateSelected, setDateSelected] = useState(
    preDateSelected && Object.keys(preDateSelected).length > 0
      ? preDateSelected
      : {
        startDate: subDays(new Date(), 30),
        endDate: subDays(new Date(), 1),
        pastStartDate: subDays(new Date(), 60),
        pastEndDate: subDays(new Date(), 31),
        key: 'Last 30 Days'
      }
  );
  
  // Add loading state to prevent rendering until data is ready
  const [isInitialized, setIsInitialized] = useState(false);
  const [disableButton, setDisableButton] = useState(true);
  const [selectedDateRange, setSelectedDateRange] = useState(
    preDateSelected && Object.keys(preDateSelected).length > 0
      ? [preDateSelected]
      : [
        {
          startDate: subDays(new Date(), 30),
          endDate: subDays(new Date(), 1),
          pastStartDate: subDays(new Date(), 60),
          pastEndDate: subDays(new Date(), 31),
          key: 'Last 30 Days'
        }
      ]
  );

  const { payloadState } = usePayloadContext();



  const doUpdateDayOrHourList = useCallback((hours) => {
      setHoursList(hours)
      setTempDateRange((prev) => ({
        ...prev,
        hourFilter: hours
      }))
  }, []);

  // useEffect(() => {
  //   const dateRangeIsDiffered = preDateSelected?.startDate == tempDateRange?.startDate && preDateSelected?.endDate == tempDateRange?.endDate && preDateSelected?.prevStartDate == tempDateRange?.prevStartDate && preDateSelected?.prevEndDate == tempDateRange?.prevEndDate
  //   if (tempDateRange?.key === preDateSelected?.key && !dateRangeIsDiffered) {
  //     setDisableButton(true);
  //   } else {
  //     setDisableButton(false);
  //   }
  // }, [tempDateRange, preDateSelected]);

  useEffect(() => {
    if (
      masterSearchCalendar !== 1 && // Prevent reset in masterSearchCalendar mode
      payloadState.dateRange &&
      Object.keys(payloadState.dateRange).length > 0 &&
      payloadState.dateRange.startDate && // Ensure startDate exists
      payloadState.dateRange.endDate && // Ensure endDate exists
      dateSelected &&
      Object.keys(dateSelected).length > 0 &&
      // JSON.stringify(payloadState.dateRange) != JSON.stringify(dateSelected) &&
      !isEqual(payloadState.dateRange, dateSelected) &&
      !fromTable
    ) {
      setSelectedDateRange([payloadState.dateRange]);
      setDateSelected(payloadState.dateRange);
      setDoCompare(
        showCompareInfo && payloadState.dateRange && Object.keys(payloadState.dateRange).length > 0 && 'compare' in payloadState.dateRange
          ? payloadState.dateRange?.compare
          : false
      );
      doCompareSelected(
        showCompareInfo && payloadState.dateRange && Object.keys(payloadState.dateRange).length > 0 && 'compare' in payloadState.dateRange
          ? payloadState.dateRange?.compare
          : false
      );
    }
  }, [payloadState.dateRange, dateSelected, masterSearchCalendar]);

  useEffect(() => {
    if (showDatepicker) {
      commonDispatch({
        type: 'UPDATE_OVERLAY_MODAL',
        payload: {
          modal: {
            show: true,
            closeCallBack: () => {
              setShowdatepicker(false);
            }
          }
        }
      });
      document.body.classList.add('overflow-hidden');
    } else {
      commonDispatch({
        type: 'UPDATE_OVERLAY_MODAL',
        payload: {
          modal: {
            show: false,
            closeCallBack: () => { }
          }
        }
      });
      document.body.classList.remove('overflow-hidden');
    }
  }, [showDatepicker]);

  const isDirectDateRef = useRef(JSON.stringify(preDateSelected));

  useEffect(() => {
    if (isDirectCalendar === true && isDirectDateRef.current != JSON.stringify(preDateSelected)) {
      isDirectDateRef.current = JSON.stringify(preDateSelected);

      setTempDateRange({
        ...preDateSelected,
        ...(isDirectType != 'between' ? { endDate: preDateSelected.startDate } : {}),
        dates: [],
        compareDates: []
      });

      setDateSelected({
        ...preDateSelected,
        ...(isDirectType != 'between' ? { endDate: preDateSelected.startDate } : {}),
        key: 'Custom'
      });

      setSelectedDateRange([
        {
          ...preDateSelected,
          ...(isDirectType != 'between' ? { endDate: preDateSelected.startDate } : {}),
          key: 'Custom'
        }
      ]);
    }

  }, [isDirectType, isDirectCalendar, preDateSelected]);
  
  const updateDateRangeFromCustom = (type, range) => {
    if (type == 'start') {
      const dateInfo = {
        ...tempDateRange,
        pastStartDate: range.start,
        pastEndDate: range.end,
        key: 'Custom'
      };

      console.log('dateInfo', { dateInfo });

      setTempDateRange(dateInfo);

      setSelectedDateRange([{ ...dateInfo, key: '' }]);
    } else if (type == 'end') {
      const dateInfo = {
        ...tempDateRange,
        pastStartDate: range.start,
        pastEndDate: range.end,
        key: 'Custom'
      };

      console.log('dateInfo', { dateInfo });

      setTempDateRange(dateInfo);

      setSelectedDateRange([{ ...dateInfo, key: '' }]);
    }
  };

  const isSameDateApplied = useMemo(() => {
    return payloadState?.dateRange?.startDate &&
      payloadState?.dateRange?.endDate &&
      payloadState?.dateRange?.pastStartDate &&
      payloadState?.dateRange?.pastEndDate &&
      tempDateRange?.startDate &&
      tempDateRange?.endDate &&
      tempDateRange?.pastStartDate &&
      tempDateRange?.pastEndDate &&
      format(new Date(payloadState?.dateRange?.startDate), 'yyyy-mm-dd') === format(new Date(tempDateRange?.startDate), 'yyyy-mm-dd') &&
      format(new Date(payloadState?.dateRange?.endDate), 'yyyy-mm-dd') === format(new Date(tempDateRange?.endDate), 'yyyy-mm-dd') &&
      format(new Date(payloadState?.dateRange?.pastStartDate), 'yyyy-mm-dd') === format(new Date(tempDateRange?.pastStartDate), 'yyyy-mm-dd') &&
      format(new Date(payloadState?.dateRange?.pastEndDate), 'yyyy-mm-dd') === format(new Date(tempDateRange?.pastEndDate), 'yyyy-mm-dd') &&
      tempDateRange?.key === payloadState?.dateRange?.key &&
      doCompare === preDateSelected?.compare
      ? true
      : false;
  }, [
    payloadState?.dateRange?.startDate,
    payloadState?.dateRange?.endDate,
    payloadState?.dateRange?.pastStartDate,
    payloadState?.dateRange?.pastEndDate,
    tempDateRange?.startDate,
    tempDateRange?.endDate,
    tempDateRange?.pastStartDate,
    tempDateRange?.pastEndDate,
    tempDateRange?.key,
    payloadState?.dateRange?.key,
    doCompare,
    preDateSelected?.compare
  ]);

  const isSameHourApplied = useMemo(() => {
    return JSON.stringify(tempDateRange?.hourFilter) == JSON.stringify(payloadState?.dateRange?.hourFilter) ? true : false
  }, [JSON.stringify(tempDateRange?.hourFilter), JSON.stringify(payloadState?.dateRange?.hourFilter)]);

  const lastMonth = new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1);

  const timeZoneTitle = useMemo(() => {
    return getTimeZone()
  }, []);

  const timeZoneAbbrv = useMemo(() => {
    return getTimeZoneAbrv()
  }, []);

  const selectedRangeDates = useMemo(() => {
    return [...selectedDateRange].concat([
      ...(doCompare == 1 && selectedDateRange?.length > 0
        ? [
          {
            startDate: dateFormat(selectedDateRange[0].pastStartDate),
            endDate: dateFormat(selectedDateRange[0].pastEndDate),
            color: '#ACACAC'
          }
        ]
        : [])
    ])
  }, [doCompare, selectedDateRange])

  useEffect(() => {
    if (dateRangePickerRef.current?.dateRange?.calendar) {
      const focusDate = new Date(tempDateRange?.startDate)
      dateRangePickerRef.current.dateRange.calendar.focusToDate(focusDate);
    }
  }, [tempDateRange?.startDate]);

  const DatePicker = (
    <Popover style={{ zIndex: '10000' }}>
      <div
        className={`date-picker-modal-one theme_date_pcker week-calendar-modal ${defaultCalendar ? calendarClasses : ''} ${selectedDateRange.length > 1 ? 'hide_range' : ''
          } bg-white`}
      >
        {/* LEFT SIDE */}
        <div className="static_rng pt-3 pb-2 pl-2">
          <h3 className="f-0092FF f-12 f-w-600 px-2">Week Presets</h3>
          <ul>
            {/* [ ...(isSnapshot === 1 ? [ { label: "Today" } ] : []) ].concat(SQP_CALENDAR_OPTIONS) */}
            {SQP_CALENDAR_OPTIONS
              .filter(item => !isAudienceCreation || (item?.label !== 'This Year' && item?.label !== 'Last Year' && item?.label !== 'Lifetime'))
              .concat(isAudienceCreation ? [{ label: 'Last 60 Days' }, { label: 'Last 90 Days' }] : [])
              .concat(commonState?.dateInfo?.start_date ? [{ label: 'Lifetime' }] : [])
              .map((date_range_row, date_range_index) => {
                return (
                  <li
                    key={`RANGE_${date_range_index}`}
                    className={`${tempDateRange?.key === date_range_row?.label ? 'active' : ''} cursor-pointer f-12`}
                    onClick={() => {
                      updateRangeView({ key: date_range_row?.label }, 1);
                      setDisableButton(false);
                    }}
                  >
                    {tempDateRange?.key === date_range_row?.label || (tempDateRange?.key == '' && date_range_row?.label == 'Custom') ? (
                      <img src={TickIcon} alt="select-date" className="slcd-dat" />
                    ) : null}
                    {date_range_row?.label}
                  </li>
                );
              })}
              {isAudienceCreation ? (
                <li className={`cursor-pointer f-12 audience-custom-days ${tempDateRange?.key == 'Last ' + audienceCustomDays + ' Days' ? 'active audience-custom-days-active' : ''}`} onClick={() => {
                  updateRangeView({ key: 'Last ' + audienceCustomDays + ' Days' }, 1, true, audienceCustomDays);
                  setDisableButton(false);
                }}>
                    Last 
                    <input
                      type="number"
                      className="f-12 f-w-400 audience-custom-number-input"
                      value={audienceCustomDays}
                      onKeyDown={(e) => {
                        if (['e', 'E', '+', '-','.'].includes(e.key)) {
                          e.preventDefault();
                        }
                      }}
                      onChange={(e) => {
                        let value = e.target.value;
                        if (value === '') {
                          setAudienceCustomDays('');
                          return;
                        }
                        if (value.includes('.')) return;
                        const numericValue = Math.min(365, Number(value));
                        const disallowedValues = [7, 14, 30, 60, 90];
                        if (
                          isNaN(numericValue) ||
                          numericValue < 1 ||
                          numericValue > 365 ||
                          disallowedValues.includes(numericValue)
                        ) {
                          return;
                        }
                        setAudienceCustomDays(numericValue);
                        updateRangeView({ key: 'Last ' + numericValue + ' Days' }, 1, true, numericValue);
                        setDisableButton(false);
                      }}
                    />
                    Days
                </li>
              ) : null}
              {/* Custom Weeks Input for SQP */}
              <li className={`cursor-pointer f-12 custom-weeks-input ${tempDateRange?.key == 'Last ' + customWeeks + ' Weeks' ? 'active custom-weeks-active' : ''}`} onClick={() => {
                updateRangeView({ key: 'Last ' + customWeeks + ' Weeks' }, 1, false, 0, customWeeks);
                setDisableButton(false);
              }}>
                  Last 
                  <input
                    type="number"
                    className="f-12 f-w-400 custom-weeks-number-input"
                    value={customWeeks}
                    min="1"
                    max="52"
                    onKeyDown={(e) => {
                      if (['e', 'E', '+', '-','.'].includes(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    onChange={(e) => {
                      let value = e.target.value;
                      if (value === '') {
                        setCustomWeeks('');
                        return;
                      }
                      if (value.includes('.')) return;
                      const numericValue = Math.min(52, Math.max(1, Number(value)));
                      if (
                        isNaN(numericValue) ||
                        numericValue < 1 ||
                        numericValue > 52
                      ) {
                        return;
                      }
                      setCustomWeeks(numericValue);
                      updateRangeView({ key: 'Last ' + numericValue + ' Weeks' }, 1, false, 0, numericValue);
                      setDisableButton(false);
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />
                  Weeks
              </li>
          </ul>
        </div>
        {/* LEFT SIDE */}

        {/* RIGHT SIDE */}
        <section className="px-2">
          <DateRangePicker
            ref={dateRangePickerRef}
            // moveRangeOnFirstSelection={true}
            // retainEndDateOnFirstSelection={true}
            editableDateInputs={true}
            calendarFocus={isThisMonth(tempDateRange?.startDate) == true ? 'backwards' : 'forwards'}
            // locale={enGB}
            ranges={selectedRangeDates}
            months={2}
            // shownDate={new Date(lastMonth)}
            // initialFocusedRange={{
            //   startDate: subMonths(new Date(), 1),
            //   endDate: new Date(),
            //   key:'Default'
            // }}
            onChange={async (item) => {
              console.log('CHANGE-DATE-RANGE', item);
              setDisableButton(false);
              if(isAmazonMarketingStream) {
                const selectedRange = item.range2 || item.Custom || item.Today || item.range1 || item.selection;
                if (selectedRange) {
                  const startDate = dateFormat(selectedRange.startDate);
                  const endDate = dateFormat(selectedRange.endDate);
                    setSelectedDateRange([
                      {
                        startDate: startDate,
                        endDate: endDate,
                        key: 'Custom'
                      }
                    ]);
                    updateRangeView({
                      startDate: startDate,
                      endDate: endDate,
                      key: 'Custom'
                    });
                  
                }
              } else if (item.range1) {
                console.log('item.range1', item);
                const snapped = snapToIsoWeeks(item.range1.startDate, item.range1.endDate);
                const comparison = calculateComparisonPeriod(snapped.weekStart, snapped.weekEnd);
                setSelectedDateRange([
                  {
                    startDate: dateFormat(snapped.weekStart),
                    endDate: dateFormat(snapped.weekEnd),
                    pastStartDate: dateFormat(comparison.pastStartDate),
                    pastEndDate: dateFormat(comparison.pastEndDate),
                    key: 'Custom'
                  }
                ]);
                updateRangeView({
                  startDate: dateFormat(snapped.weekStart),
                  endDate: dateFormat(snapped.weekEnd),
                  pastStartDate: dateFormat(comparison.pastStartDate),
                  pastEndDate: dateFormat(comparison.pastEndDate),
                  key: 'Custom'
                });
              } else if (item.selection) {
                const snapped = snapToIsoWeeks(item.selection.startDate, item.selection.endDate);
                const comparison = calculateComparisonPeriod(snapped.weekStart, snapped.weekEnd);
                setSelectedDateRange([
                  {
                    startDate: dateFormat(snapped.weekStart),
                    endDate: dateFormat(snapped.weekEnd),
                    pastStartDate: dateFormat(comparison.pastStartDate),
                    pastEndDate: dateFormat(comparison.pastEndDate),
                    key: 'Custom'
                  }
                ]);
                updateRangeView({
                  startDate: dateFormat(snapped.weekStart),
                  endDate: dateFormat(snapped.weekEnd),
                  pastStartDate: dateFormat(comparison.pastStartDate),
                  pastEndDate: dateFormat(comparison.pastEndDate),
                  key: 'Custom'
                });
              } else if (!item.range) {
                console.log('!item.range', item);
                console.log({ ...Object.values(item)[0], key: 'Custom' });
                // setSelectedDateRange([]);
                const first = Object.values(item)[0];
                const snapped = snapToIsoWeeks(first.startDate, first.endDate || first.startDate);
                const comparison = calculateComparisonPeriod(snapped.weekStart, snapped.weekEnd);
                updateRangeView({ 
                  startDate: snapped.weekStart, 
                  endDate: snapped.weekEnd, 
                  pastStartDate: comparison.pastStartDate,
                  pastEndDate: comparison.pastEndDate,
                  key: 'Custom' 
                });
              }
              // if(item && item?.range1) updateFocus()
            }}
            // onCalendarClose={() => {
            //   updateFocus()
            // }}
            direction="horizontal"
            maxDate={maxDate !== undefined ? maxDate : (isAmazonSQP ? getLastCompleteSaturday() : undefined)}
            staticRanges={[]}
          />
          {!isDirectCalendar ? (
            <div className="dat-pic-close bg-white d-flex flex-column px-2 pb-0">
              {!customSelectHidden && <div className="d-flex align-items-center">
                <Row className="wid-614">
                  <Col sm={6}>
                    <h4 className="f-10 f-w-600 mb-2">Selected Date Range</h4>
                  </Col>
                  <Col sm={6}>{isSnapshot && doCompare && showCompareInfo ? <h4 className={`f-10 f-w-600 mb-2`}>Compared Date Range </h4> : null}</Col>
                  <Col sm={6}>
                    <Row>
                      <Col sm={6} className={`pr-2 position-relative ${tempDateRange?.key === 'Custom' ? 'disabled' : 'disabled'}`}>
                        <label className="f-333843 f-10 f-w-400 mb-1">From</label>
                        <div className="position-relative">
                          <img src={Calender} alt="date_picker" className="wid-20 mr-2 prefix-icon z-500 top-9" />
                          <input
                            type="date"
                            className="form-field cus_date-select"
                            value={format(selectedDateRange[0]?.startDate, 'yyyy-MM-dd')}
                            onChange={() => { }}
                          />
                        </div>
                      </Col>
                      <Col sm={6} className={`pl-1 position-relative ${tempDateRange?.key === 'Custom' ? 'disabled' : 'disabled'}`}>
                        <label className="f-333843 f-10 f-w-400 mb-1">To</label>
                        <div className="position-relative">
                          <img src={Calender} alt="date_picker" className="wid-20 mr-2 prefix-icon z-500 top-9" />
                          <input
                            type="date"
                            className="form-field cus_date-select"
                            value={format(selectedDateRange[0]?.endDate, 'yyyy-MM-dd')}
                            onChange={() => { }}
                          />
                        </div>
                      </Col>
                    </Row>
                  </Col>
                  {isSnapshot && tempDateRange?.key != 'Lifetime' && showCompareInfo ? (
                    <Col sm={6} className={`${!isSnapshot ? 'card-hidden' : ''}`}>
                      {doCompare ? (
                        <Row>
                          <Col sm={6} className={`pr-2 position-relative disabled ${tempDateRange?.key === 'Custom' ? '' : ''}`}>
                          {/* disabled */}
                            <label className="f-333843 f-10 f-w-400 mb-1">From</label>
                            <div className="position-relative">
                              <img src={Calender} alt="date_picker" className="wid-20 mr-2 prefix-icon z-500 top-9" />
                              <input
                                type="date"
                                className="form-field cus_date-select"
                                max={format(subDays(tempDateRange?.startDate, 1), 'yyyy-MM-dd')}
                                value={format(selectedDateRange[0]?.pastStartDate, 'yyyy-MM-dd')}
                                onChange={(event) => {
                                  const duration =
                                    eachDayOfInterval({ start: tempDateRange.startDate, end: tempDateRange.endDate }).length - 1;
                                  const addCustomDays = addDays(new Date(event.target.value), duration);
                                  console.log('duration', { duration, start: new Date(event.target.value), end: addCustomDays });
                                  if (
                                    new Date(event.target.value) < new Date() &&
                                    new Date(addCustomDays) < new Date(tempDateRange.startDate)
                                  ) {
                                    updateDateRangeFromCustom('start', {
                                      start: new Date(event.target.value),
                                      end: addCustomDays
                                    });
                                    setStartFailureMessage('');
                                  } else {
                                    console.log('Duration Not Matched');
                                    event.preventDefault();
                                    setStartFailureMessage('Please select the valid date');
                                    setTimeout(() => {
                                      setStartFailureMessage('');
                                    }, 3000);
                                  }
                                }}
                              />
                            </div>
                            {startFailureMessage != '' ? <p className="text-danger f-10">{startFailureMessage}</p> : ''}
                          </Col>
                          <Col sm={6} className={`pl-1 position-relative disabled ${tempDateRange?.key === 'Custom' ? '' : ''}`}>
                          {/* disabled */}
                            <label className="f-333843 f-10 f-w-400 mb-1">To</label>
                            <div className="position-relative">
                              <img src={Calender} alt="date_picker" className="wid-20 mr-2 prefix-icon z-500 top-9" />
                              <input
                                type="date"
                                className="form-field cus_date-select"
                                max={format(subDays(tempDateRange?.startDate, 1), 'yyyy-MM-dd')}
                                value={format(selectedDateRange[0]?.pastEndDate, 'yyyy-MM-dd')}
                                onChange={(event) => {
                                  const duration =
                                    eachDayOfInterval({ start: tempDateRange.startDate, end: tempDateRange.endDate }).length - 1;
                                  const subCustomDays = subDays(new Date(event.target.value), duration);
                                  console.log('duration', { duration, start: new Date(event.target.value), end: subCustomDays });
                                  if (new Date(event.target.value) < new Date() && subCustomDays < new Date(tempDateRange.startDate)) {
                                    updateDateRangeFromCustom('start', {
                                      start: subCustomDays,
                                      end: new Date(event.target.value)
                                    });
                                    setEndFailureMessage('');
                                  } else {
                                    console.log('duration Not Matched');
                                    event.preventDefault();
                                    setEndFailureMessage('Please select the valid date');
                                    setTimeout(() => {
                                      setEndFailureMessage('');
                                    }, 3000);
                                  }
                                }}
                              />
                            </div>
                            {endFailureMessage != '' ? <p className="text-danger f-10">{endFailureMessage}</p> : ''}
                          </Col>
                        </Row>
                      ) : null}
                    </Col>
                  ) : null}
                </Row>
              </div>}
              <section
                className={`w-100 d-flex align-items-center mt-2 py-2 border-top justify-content-${fromTable != 1 && isSnapshot && tempDateRange?.key != 'Lifetime' ? 'between' : 'end'
                  }`}
              >
                <div className='d-flex align-items-center gap-10'>
                  {fromTable != 1 && isSnapshot && tempDateRange?.key != 'Lifetime' && isAmazonSQP ? (
                    <div className="d-flex align-items-center gap-15 mr-5 pr-3">
                      <Form>
                        <Form.Check
                          className="f-12 d-flex align-items-center f-10 f-w-600 date_tog"
                          type="switch"
                          id="custom-switch"
                          onChange={(event) => {
                            if (event.target.checked === true) {
                              setDoCompare(1);
                              setDisableButton(false);
                            } else if (event.target.checked === false) {
                              setDoCompare(0);
                              setDisableButton(false);
                            }
                            // updateFocus()
                          }}
                          checked={doCompare == 1}
                          label="Compare"
                        />
                      </Form>
                    </div>
                  ) : null}
                  {/* {window.location.href.indexOf('amazon/marketing-stream') > -1 && <div>
                    <HourlyFilterData hoursList={hoursList} setHoursList={setHoursList} doUpdateDayOrHourList={doUpdateDayOrHourList} />
                  </div>} */}
                </div>
                <div className="d-flex align-items-center gap-20">
                <p className='f-w-500 f-10' >  {timeZoneTitle} <span className='text-right ml-1 no-wrap' style={{ float: 'right' }}>( {timeZoneAbbrv} )</span>  </p>
                  <p
                    className="gray_btn cursor-pointer f-12 f-w-400 border-0 dat_btn mt-0"
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent event bubbling
                      setShowdatepicker(false);
                    }}
                  >
                    Cancel
                  </p>
                  <p
                    className={`blue_btn m-0 cursor-pointer f-12 f-w-400 dat_btn ${disableButton || (isSameDateApplied && isSameHourApplied) ? 'disabled' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent event bubbling
                      saveDateRange();
                    }}
                  >
                    Apply
                  </p>
                </div>
              </section>
            </div>
          ) : null}
        </section>
        {/* RIGHT SIDE */}
      </div>
    </Popover>
  );

  useEffect(() => {
    setCrossFilterPayload(crossFilter);
  }, [JSON.stringify(crossFilter)]);

  // const updateFocus = useCallback(() => {
  //   console.log("dateRangePickerRef.current", dateRangePickerRef.current)
  //   setTimeout(() => {
  //     dateRangePickerRef.current?.dateRange?.calendar?.focusToDate(new Date(tempDateRange?.startDate));
  //   }, 1000);
  // }, [tempDateRange?.startDate, tempDateRange?.endDate, tempDateRange?.pastStartDate, tempDateRange?.pastEndDate, doCompare]);


  const updateRangeView = useCallback((rangeInfo, from = 0, isAudienceCustomCreation = false, audienceSelectDays = 0, customWeeksValue = 0) => {
    // const rangeInfo = selectedDateRange[0];
    console.log('updateRangeView', rangeInfo);

    let dateInfo = {};
    const currentDate = new Date();
    const hourlyPayload = {
      hourFilter: hoursList
    }

    console.log("hourlyPayload", hourlyPayload)

    if ((rangeInfo.key === 'Today' || rangeInfo.key == 'Custom') && from == 1) {
      if (rangeInfo.key == 'Custom') {
        const dateInfo = { ...tempDateRange, key: 'Custom', ...hourlyPayload };
        setTempDateRange(dateInfo);
        setSelectedDateRange([{ ...dateInfo, key: '' }]);
      } else {
        const currentDateInZone = new Date(zoneToData(new Date()));
        const today = currentDateInZone;
        const yesterday = subDays(currentDateInZone, 1);
        dateInfo = {
          startDate: new Date(today),
          endDate: new Date(today),
          pastStartDate: new Date(yesterday),
          pastEndDate: new Date(yesterday),
          key: rangeInfo.key,
          ...hourlyPayload
        };
        setTempDateRange(dateInfo);
        setSelectedDateRange([{ ...dateInfo, key: '' }]);
      }
    } else if (from == 1 && isAudienceCustomCreation && isAudienceCreation && audienceSelectDays !== undefined && audienceSelectDays !== null) {
      const currentDateInZone = new Date(zoneToData(new Date()));
      const startDate = subDays(currentDateInZone, audienceSelectDays);
      const endDate = subDays(currentDateInZone, 1);
      const pastStartDate = subDays(currentDateInZone, audienceSelectDays * 2);
      const pastEndDate = subDays(currentDateInZone, audienceSelectDays + 1);
      dateInfo = {
        startDate: new Date(startDate),
        endDate: new Date(endDate),
        pastStartDate: new Date(pastStartDate),
        pastEndDate: new Date(pastEndDate),
        key: 'Last ' + audienceSelectDays + ' Days',
        ...hourlyPayload
      };
      setTempDateRange(dateInfo);
      setSelectedDateRange([{ ...dateInfo, key: '' }]);
    } else if (rangeInfo.key === 'Yesterday' && from == 1) {
      const currentDateInZone = new Date(zoneToData(new Date()));

      const yesterday = subDays(currentDateInZone, 1);
      const dayBeforeYesterday = subDays(currentDateInZone, 2);
      dateInfo = {
        startDate: new Date(yesterday),
        endDate: new Date(yesterday),
        pastStartDate: new Date(dayBeforeYesterday),
        pastEndDate: new Date(dayBeforeYesterday),
        key: 'Yesterday',
        ...hourlyPayload
      };
      setTempDateRange(dateInfo);
      setSelectedDateRange([{ ...dateInfo, key: '' }]);
    } else if (rangeInfo.key === 'This Month' && from == 1) {
      const currentDateInZone = new Date(zoneToData(new Date()));

      const startOfThisMonth = startOfMonth(currentDateInZone);
      const yesterdayInZone = addDays(currentDateInZone, -1);

      const endOfThisMonth = yesterdayInZone < startOfThisMonth
        ? startOfThisMonth
        : yesterdayInZone;

      // Calculate the number of days in the current selected range
      const currentDays = differenceInDays(endOfThisMonth, startOfThisMonth) + 1;

      const prevStartOfMonth = startOfMonth(subMonths(currentDateInZone, 1));
      const prevEndOfMonth = addDays(prevStartOfMonth, currentDays - 1);

      dateInfo = {
        startDate: startOfThisMonth,
        endDate: endOfThisMonth,
        pastStartDate: prevStartOfMonth,
        pastEndDate: prevEndOfMonth,
        key: 'This Month',
        ...hourlyPayload
      };
      setTempDateRange(dateInfo);
      setSelectedDateRange([{ ...dateInfo, key: '' }]);
    } else if (rangeInfo.key === 'Last 30 Days' && from == 1) {
      const currentDateInZone = new Date(zoneToData(new Date()));

      const startDate = subDays(currentDateInZone, 30);
      const endDate = subDays(currentDateInZone, 1);
      const pastStartDate = subDays(currentDateInZone, 60);
      const pastEndDate = subDays(currentDateInZone, 31);
      dateInfo = {
        startDate: new Date(startDate),
        endDate: new Date(endDate),
        pastStartDate: new Date(pastStartDate),
        pastEndDate: new Date(pastEndDate),
        key: 'Last 30 Days',
        ...hourlyPayload
      };
      setTempDateRange(dateInfo);
      setSelectedDateRange([{ ...dateInfo, key: '' }]);
    } else if (rangeInfo.key === 'Last Month' && from == 1) {
      // Get the start and end dates for the current month
      const currentDateInZone = new Date(zoneToData(new Date()));

      // Get the start and end dates for the previous month in user timezone
      const startDate = startOfMonth(subMonths(currentDateInZone, 1));
      const endDate = endOfMonth(subMonths(currentDateInZone, 1));

      // Calculate the number of days in that month
      const numberOfDays = differenceInDays(endDate, startDate) + 1;

      // Get the start of the month before that
      const pastStartDate = startOfMonth(subMonths(currentDateInZone, 2));
      let tentativeEndDate = addDays(pastStartDate, numberOfDays - 1);
      const pastEndDate = isAfter(tentativeEndDate, endOfMonth(pastStartDate))
        ? endOfMonth(pastStartDate)
        : tentativeEndDate;
    
      dateInfo = {
        startDate: new Date(startDate),
        endDate: new Date(endDate),
        pastStartDate: new Date(pastStartDate),
        pastEndDate: new Date(pastEndDate),
        key: 'Last Month',
        ...hourlyPayload
      };

      setTempDateRange(dateInfo);
      setSelectedDateRange([{ ...dateInfo, key: '' }]);
    } else if (rangeInfo.key === 'Last 30 Days' && from == 1) {
      const currentDateInZone = new Date(zoneToData(new Date())); // Adjust to user's timezone

      const startDate = subDays(currentDateInZone, 30);
      const endDate = subDays(currentDateInZone, 1);
      const pastStartDate = subDays(currentDateInZone, 60);
      const pastEndDate = subDays(currentDateInZone, 31);
      dateInfo = {
        startDate: new Date(zoneToData(startDate)),
        endDate: new Date(zoneToData(endDate)),
        pastStartDate: new Date(zoneToData(pastStartDate)),
        pastEndDate: new Date(zoneToData(pastEndDate)),
        key: 'Last 30 Days',
        ...hourlyPayload
      };
      setTempDateRange(dateInfo);
      setSelectedDateRange([{ ...dateInfo, key: '' }]);
    } else if (rangeInfo.key === 'This Year' && from == 1) {
      const currentDateInZone = new Date(zoneToData(new Date())); // Adjust current date to user's timezone

      const startOfThisYear = new Date(currentDateInZone.getFullYear(), 0, 1);
      const endOfThisYear = subDays(currentDateInZone, 1); // Yesterday

      const pastStartDate = new Date(startOfThisYear);
      pastStartDate.setFullYear(pastStartDate.getFullYear() - 1);

      const pastEndDate = new Date(endOfThisYear);
      pastEndDate.setFullYear(pastEndDate.getFullYear() - 1);

      dateInfo = {
        startDate: new Date(startOfThisYear),
        endDate: new Date(endOfThisYear),
        pastStartDate: new Date(pastStartDate),
        pastEndDate: new Date(pastEndDate),
        key: 'This Year',
        ...hourlyPayload
      };

      console.log('dateInfo', { dateInfo });
      setTempDateRange(dateInfo);
      setSelectedDateRange([{ ...dateInfo, key: '' }]);
    } else if (rangeInfo.key === 'Last Year' && from == 1) {
      const currentDateInZone = new Date(zoneToData(new Date()));

      const lastYearStart = startOfYear(subYears(currentDateInZone, 1));
      const lastYearEnd = endOfYear(subYears(currentDateInZone, 1));

      const pastLastYearStart = startOfYear(subYears(currentDateInZone, 2));
      const pastLastYearEnd = endOfYear(subYears(currentDateInZone, 2));

      dateInfo = {
        startDate: new Date(lastYearStart),
        endDate: new Date(lastYearEnd),
        pastStartDate: new Date(pastLastYearStart),
        pastEndDate: new Date(pastLastYearEnd),
        key: 'Last Year',
        ...hourlyPayload
      };

      setTempDateRange(dateInfo);
      setSelectedDateRange([{ ...dateInfo, key: '' }]);
    } else if (rangeInfo.key === 'Last 7 Days' && from == 1) {
      const currentDateInZone = new Date(zoneToData(new Date())); // user's current datetime in their timezone

      const startDate = subDays(currentDateInZone, 7);
      const endDate = subDays(currentDateInZone, 1);
      const pastStartDate = subDays(currentDateInZone, 14);
      const pastEndDate = subDays(currentDateInZone, 8);
      dateInfo = {
        startDate: new Date(startDate),
        endDate: new Date(endDate),
        pastStartDate: new Date(pastStartDate),
        pastEndDate: new Date(pastEndDate),
        key: 'Last 7 Days',
        ...hourlyPayload
      };
      setTempDateRange(dateInfo);
      setSelectedDateRange([{ ...dateInfo, key: '' }]);
    } else if (rangeInfo.key === 'Last 14 Days' && from == 1) {
      const currentDateInZone = new Date(zoneToData(new Date())); // Adjust to user's timezone

      const startDate = subDays(currentDateInZone, 14);
      const endDate = subDays(currentDateInZone, 1);
      const pastStartDate = subDays(currentDateInZone, 28);
      const pastEndDate = subDays(currentDateInZone, 15);
      dateInfo = {
        startDate: new Date(startDate),
        endDate: new Date(endDate),
        pastStartDate: new Date(pastStartDate),
        pastEndDate: new Date(pastEndDate),
        key: 'Last 14 Days',
        ...hourlyPayload
      };
      setTempDateRange(dateInfo);
      setSelectedDateRange([{ ...dateInfo, key: '' }]);
    } else if (rangeInfo.key === 'This Week' && from == 1) {
      // Get profile timezone from localStorage (account timezone, not system timezone)
      const profileTimezone = getTimeZone();
      
      // Calculate last complete Saturday in profile timezone
      const nowInProfileTz = moment().tz(profileTimezone);
      const currentDayOfWeek = nowInProfileTz.day(); // 0..6 (Sun..Sat)
      let lastCompleteSaturdayInProfileTz;
      if (currentDayOfWeek === 6) {
        lastCompleteSaturdayInProfileTz = nowInProfileTz.clone().subtract(7, 'days');
      } else {
        lastCompleteSaturdayInProfileTz = nowInProfileTz.clone().subtract(currentDayOfWeek + 1, 'days');
      }
      
      // Ensure it's the end of week (Saturday) in profile timezone
      const saturdayDate = lastCompleteSaturdayInProfileTz.clone().day(6);
      const saturdayDateString = saturdayDate.format('YYYY-MM-DD');
      const [year, month, day] = saturdayDateString.split('-').map(Number);
      let lastCompleteSaturday = new Date(year, month - 1, day);
      
      // Use sqp_end_date from database if available
      // Always use the minimum of: database end date OR last complete Saturday in profile timezone
      if (isAmazonSQP && commonState?.dateInfo?.sqp_end_date) {
        const sqpEndDate = new Date(commonState.dateInfo.sqp_end_date);
        if (sqpEndDate instanceof Date && !isNaN(sqpEndDate.getTime())) {
          // Convert to profile timezone and ensure it's the end of week (Saturday)
          const dateInProfileTz = moment(sqpEndDate).tz(profileTimezone);
          const saturdayInProfileTz = dateInProfileTz.clone().day(6);
          const saturdayDateString = saturdayInProfileTz.format('YYYY-MM-DD');
          const [year, month, day] = saturdayDateString.split('-').map(Number);
          const dbEndDateNormalized = new Date(year, month - 1, day);
          
          // Use the minimum of database date or profile's last complete Saturday
          lastCompleteSaturday = dbEndDateNormalized < lastCompleteSaturday ? dbEndDateNormalized : lastCompleteSaturday;
        }
      }
      
      // This week: from Sunday to last complete Saturday (ensures full week)
      const endOfWk = lastCompleteSaturday; // Already normalized to Saturday
      const startOfWk = startOfWeek(endOfWk, { weekStartsOn: 0 }); // Always Sunday
      
      // Previous week: complete Sun-Sat week before this week
      const prevStartOfWeek = startOfWeek(subWeeks(startOfWk, 1), { weekStartsOn: 0 });
      const prevEndOfWeek = endOfWeek(subWeeks(startOfWk, 1), { weekStartsOn: 0 });

      dateInfo = {
        startDate: new Date(startOfWk),
        endDate: new Date(endOfWk),
        pastStartDate: new Date(prevStartOfWeek),
        pastEndDate: new Date(prevEndOfWeek),
        key: 'This Week',
        ...hourlyPayload
      };
      setTempDateRange(dateInfo);

      setSelectedDateRange([{ ...dateInfo, key: '' }]);
    } else if (rangeInfo.key === 'Last Week' && from == 1) {
      // Get profile timezone from localStorage (account timezone, not system timezone)
      const profileTimezone = getTimeZone();
      
      // Calculate last complete Saturday in profile timezone
      const nowInProfileTz = moment().tz(profileTimezone);
      const currentDayOfWeek = nowInProfileTz.day(); // 0..6 (Sun..Sat)
      let lastCompleteSaturdayInProfileTz;
      if (currentDayOfWeek === 6) {
        lastCompleteSaturdayInProfileTz = nowInProfileTz.clone().subtract(7, 'days');
      } else {
        lastCompleteSaturdayInProfileTz = nowInProfileTz.clone().subtract(currentDayOfWeek + 1, 'days');
      }
      
      // Ensure it's the end of week (Saturday) in profile timezone
      const saturdayDate = lastCompleteSaturdayInProfileTz.clone().day(6);
      const saturdayDateString = saturdayDate.format('YYYY-MM-DD');
      const [year, month, day] = saturdayDateString.split('-').map(Number);
      let lastCompleteSaturday = new Date(year, month - 1, day);
      
      // Use sqp_end_date from database if available
      // Always use the minimum of: database end date OR last complete Saturday in profile timezone
      if (isAmazonSQP && commonState?.dateInfo?.sqp_end_date) {
        const sqpEndDate = new Date(commonState.dateInfo.sqp_end_date);
        if (sqpEndDate instanceof Date && !isNaN(sqpEndDate.getTime())) {
          // Convert to profile timezone and ensure it's the end of week (Saturday)
          const dateInProfileTz = moment(sqpEndDate).tz(profileTimezone);
          const saturdayInProfileTz = dateInProfileTz.clone().day(6);
          const saturdayDateString = saturdayInProfileTz.format('YYYY-MM-DD');
          const [year, month, day] = saturdayDateString.split('-').map(Number);
          const dbEndDateNormalized = new Date(year, month - 1, day);
          
          // Use the minimum of database date or profile's last complete Saturday
          lastCompleteSaturday = dbEndDateNormalized < lastCompleteSaturday ? dbEndDateNormalized : lastCompleteSaturday;
        }
      }
      
      // Last week: complete Sun-Sat week ending on lastCompleteSaturday (ensures full week)
      const endOfLastWeek = lastCompleteSaturday; // Already normalized to Saturday
      const startOfLastWeek = startOfWeek(endOfLastWeek, { weekStartsOn: 0 }); // Always Sunday
      
      // Past week: complete Sun-Sat week before last week
      const pastEndOfWeek = subWeeks(endOfLastWeek, 1);
      const pastStartOfWeek = startOfWeek(pastEndOfWeek, { weekStartsOn: 0 });

      dateInfo = {
        startDate: new Date(startOfLastWeek),
        endDate: new Date(endOfLastWeek),
        pastStartDate: new Date(pastStartOfWeek),
        pastEndDate: new Date(pastEndOfWeek),
        key: 'Last Week',
        ...hourlyPayload
      };
      setTempDateRange(dateInfo);
      setSelectedDateRange([{ ...dateInfo, key: '' }]);
    } else if ((['Last 2 Weeks', 'Last 4 Weeks', 'Last 8 Weeks', 'Last 12 Weeks'].includes(rangeInfo.key)) && from == 1) {
      // Get profile timezone from localStorage (account timezone, not system timezone)
      const profileTimezone = getTimeZone();
      
      // Calculate last complete Saturday in profile timezone
      const nowInProfileTz = moment().tz(profileTimezone);
      const currentDayOfWeek = nowInProfileTz.day(); // 0 = Sunday, ..., 6 = Saturday
      let lastCompleteSaturdayInProfileTz;
      if (currentDayOfWeek === 6) {
        // If today is Saturday, the last complete Saturday is 7 days ago
        lastCompleteSaturdayInProfileTz = nowInProfileTz.clone().subtract(7, 'days');
      } else {
        // For any other day, find the most recent Saturday
        lastCompleteSaturdayInProfileTz = nowInProfileTz.clone().subtract(currentDayOfWeek + 1, 'days');
      }
      let endDate = endOfWeek(lastCompleteSaturdayInProfileTz.toDate(), { weekStartsOn: 0 });
      
      // Use sqp_end_date from database if available
      // Always use the minimum of: database end date OR last complete Saturday in profile timezone
      if (isAmazonSQP && commonState?.dateInfo?.sqp_end_date) {
        const sqpEndDate = new Date(commonState.dateInfo.sqp_end_date);
        if (sqpEndDate instanceof Date && !isNaN(sqpEndDate.getTime())) {
          // Convert to profile timezone and ensure it's the end of week (Saturday)
          const dateInProfileTz = moment(sqpEndDate).tz(profileTimezone);
          const dbEndDateNormalized = endOfWeek(dateInProfileTz.toDate(), { weekStartsOn: 0 });
          
          // Use the minimum of database date or profile's last complete Saturday
          endDate = dbEndDateNormalized < endDate ? dbEndDateNormalized : endDate;
        }
      }

      let weeksToSubtract = 2;
      if (rangeInfo.key === 'Last 4 Weeks') weeksToSubtract = 4;
      if (rangeInfo.key === 'Last 8 Weeks') weeksToSubtract = 8;
      if (rangeInfo.key === 'Last 12 Weeks') weeksToSubtract = 12;

      // Main period: complete Sun-Sat weeks ending on Saturday, starting on Sunday (ensures full weeks)
      // endDate is always Saturday, so calculate start by going back N-1 weeks to get Sunday
      const startDate = startOfWeek(subWeeks(endDate, weeksToSubtract - 1), { weekStartsOn: 0 });

      // Comparison period: previous same number of weeks immediately before main period
      const pastEndDate = subDays(startDate, 1);
      const pastStartDate = startOfWeek(subWeeks(pastEndDate, weeksToSubtract - 1), { weekStartsOn: 0 });

      const dateInfo = {
        startDate: new Date(startDate),
        endDate: new Date(endDate),
        pastStartDate: new Date(pastStartDate),
        pastEndDate: new Date(pastEndDate),
        key: rangeInfo.key,
        ...hourlyPayload
      };

      setTempDateRange(dateInfo);
      setSelectedDateRange([{ ...dateInfo, key: '' }]);
    } else if (rangeInfo.key && rangeInfo.key.startsWith('Last ') && rangeInfo.key.includes(' Weeks') && customWeeksValue > 0 && from == 1) {
      // Handle custom weeks input (e.g., "Last 3 Weeks", "Last 6 Weeks", etc.)
      // Get profile timezone from localStorage (account timezone, not system timezone)
      const profileTimezone = getTimeZone();
      
      // Calculate last complete Saturday in profile timezone
      const nowInProfileTz = moment().tz(profileTimezone);
      const currentDayOfWeek = nowInProfileTz.day(); // 0 = Sunday, ..., 6 = Saturday
      let lastCompleteSaturdayInProfileTz;
      if (currentDayOfWeek === 6) {
        // If today is Saturday, the last complete Saturday is 7 days ago
        lastCompleteSaturdayInProfileTz = nowInProfileTz.clone().subtract(7, 'days');
      } else {
        // For any other day, find the most recent Saturday
        lastCompleteSaturdayInProfileTz = nowInProfileTz.clone().subtract(currentDayOfWeek + 1, 'days');
      }
      let endDate = endOfWeek(lastCompleteSaturdayInProfileTz.toDate(), { weekStartsOn: 0 });
      
      // Use sqp_end_date from database if available
      // Always use the minimum of: database end date OR last complete Saturday in profile timezone
      if (isAmazonSQP && commonState?.dateInfo?.sqp_end_date) {
        const sqpEndDate = new Date(commonState.dateInfo.sqp_end_date);
        if (sqpEndDate instanceof Date && !isNaN(sqpEndDate.getTime())) {
          // Convert to profile timezone and ensure it's the end of week (Saturday)
          const dateInProfileTz = moment(sqpEndDate).tz(profileTimezone);
          const dbEndDateNormalized = endOfWeek(dateInProfileTz.toDate(), { weekStartsOn: 0 });
          
          // Use the minimum of database date or profile's last complete Saturday
          endDate = dbEndDateNormalized < endDate ? dbEndDateNormalized : endDate;
        }
      }
      
      const weeksToSubtract = customWeeksValue;

      // Main period: complete Sun-Sat weeks ending on Saturday, starting on Sunday (ensures full weeks)
      // endDate is always Saturday, so calculate start by going back N-1 weeks to get Sunday
      const startDate = startOfWeek(subWeeks(endDate, weeksToSubtract - 1), { weekStartsOn: 0 });

      // Comparison period: previous same number of weeks immediately before main period
      const pastEndDate = subDays(startDate, 1);
      const pastStartDate = startOfWeek(subWeeks(pastEndDate, weeksToSubtract - 1), { weekStartsOn: 0 });

      const dateInfo = {
        startDate: new Date(startDate),
        endDate: new Date(endDate),
        pastStartDate: new Date(pastStartDate),
        pastEndDate: new Date(pastEndDate),
        key: rangeInfo.key,
        ...hourlyPayload
      };

      setTempDateRange(dateInfo);
      setSelectedDateRange([{ ...dateInfo, key: '' }]);
    } else if (rangeInfo.key === 'Last 7 Weeks' && from == 1) {
      const currentDateInZone = new Date(zoneToData(new Date())); 
      const yesterday = subDays(currentDateInZone, 1); 
      const startDate = subWeeks(yesterday, 7); 
      const endDate = yesterday; 
    
      const pastStartDate = subWeeks(yesterday, 14); 
      const pastEndDate = subWeeks(yesterday, 7);
      dateInfo = {
        startDate: new Date(zoneToData(startDate)),
        endDate: new Date(zoneToData(endDate)),
        pastStartDate: new Date(zoneToData(pastStartDate)),
        pastEndDate: new Date(zoneToData(pastEndDate)),
        key: 'Last 7 Weeks',
        ...hourlyPayload
      };
      console.log('dateInfo', { dateInfo });
      setTempDateRange(dateInfo);
      setSelectedDateRange([{ ...dateInfo, key: '' }]);
    } else if (rangeInfo.key === 'Last 7 Months' && from == 1) {
      dateInfo = {
        startDate: new Date(zoneToData(startOfMonth(subMonths(currentDate, 7)))),
        endDate: new Date(zoneToData(endOfMonth(subMonths(currentDate, 1)))),
        pastStartDate: new Date(zoneToData(startOfMonth(subMonths(currentDate, 14)))),
        pastEndDate: new Date(zoneToData(endOfMonth(subMonths(currentDate, 8)))),
        key: 'Last 7 Months',
        ...hourlyPayload
      };
      console.log('dateInfo', { dateInfo });

      setTempDateRange(dateInfo);
      setSelectedDateRange([{ ...dateInfo, key: '' }]);
    } else if (rangeInfo.key === 'Last 7 Weekdays' && from == 1) {
      // Initialize counters
      let last7WeekdaysCount = 0;
      let beforeLast7WeekdaysCount = 0;

      // Arrays to store the date ranges
      const last7Weekdays = [];
      const beforeLast7Weekdays = [];

      // Iterate from the current date backwards to find the dates
      while (last7WeekdaysCount < 7 || beforeLast7WeekdaysCount < 7) {
        const currentDateCopy = new Date(currentDate);

        // Subtract a day from the current date
        currentDateCopy.setDate(currentDateCopy.getDate() - 1);

        // Check if the current date is a weekday (not a weekend)
        if (!isWeekend(currentDateCopy)) {
          if (last7WeekdaysCount < 7) {
            last7Weekdays.push({
              start: startOfDay(currentDateCopy),
              end: endOfDay(currentDateCopy)
            });
            last7WeekdaysCount++;
          } else if (beforeLast7WeekdaysCount < 7) {
            beforeLast7Weekdays.push({
              start: startOfDay(currentDateCopy),
              end: endOfDay(currentDateCopy)
            });
            beforeLast7WeekdaysCount++;
          }
        }
        // Move to the previous day
        currentDate.setDate(currentDate.getDate() - 1);
      }


      console.log('updateRangeView', { last7Weekdays, beforeLast7Weekdays });

      const last7Week = last7Weekdays
        .sort((rowA, rowB) => Number(rowA.start) - Number(rowB.start))
        .map((dateItem, index) => {
          return {
            startDate: new Date(zoneToData(dateItem.start)),
            endDate: new Date(zoneToData(dateItem.end)),
            pastStartDate: new Date(zoneToData(dateItem.start)),
            pastEndDate: new Date(zoneToData(dateItem.end)),
            key: '',
            ...hourlyPayload
          };
        });

      const beforeLast7Week = beforeLast7Weekdays
        .sort((rowA, rowB) => Number(rowA.start) - Number(rowB.start))
        .map((dateItem, index) => {
          return {
            startDate: new Date(zoneToData(dateItem.start)),
            endDate: new Date(zoneToData(dateItem.end)),
            pastStartDate: new Date(zoneToData(dateItem.start)),
            pastEndDate: new Date(zoneToData(dateItem.end)),
            key: '',
            ...hourlyPayload
          };
        });

      if (beforeLast7Week && last7Week) {
        const rangesSelected = beforeLast7Week.concat(last7Week);

        console.log('RANGES', rangesSelected);
        // doCompare ? rangesSelected : last7Week
        setSelectedDateRange(last7Week);

        dateInfo = {
          startDate: last7Weekdays.sort((rowA, rowB) => Number(rowA.start) - Number(rowB.start))[0].start,
          endDate: last7Weekdays.sort((rowA, rowB) => Number(rowA.start) - Number(rowB.start))[last7Weekdays.length - 1].end,
          pastStartDate: beforeLast7Weekdays.sort((rowA, rowB) => Number(rowA.start) - Number(rowB.start))[0].start,
          pastEndDate: beforeLast7Weekdays.sort((rowA, rowB) => Number(rowA.start) - Number(rowB.start))[beforeLast7Weekdays.length - 1]
            .end,
          dates: last7Week.map((row) => row.startDate),
          compareDates: beforeLast7Week.map((row) => row.startDate),
          key: 'Last 7 Weekdays',
          current: last7Week,
          previous: beforeLast7Week,
          ...hourlyPayload
        };

        setTempDateRange(dateInfo);
      }
    } else if (rangeInfo.key === 'Last 7 Weekends' && from == 1) {
      // Initialize counters
      let last7WeekdaysCount = 0;
      let beforeLast7WeekdaysCount = 0;

      // Arrays to store the date ranges
      const last7Weekdays = [];
      const beforeLast7Weekdays = [];

      // Iterate from the current date backwards to find the dates
      while (last7WeekdaysCount < 7 || beforeLast7WeekdaysCount < 7) {
        const currentDateCopy = new Date(currentDate);

        // Subtract a day from the current date
        currentDateCopy.setDate(currentDateCopy.getDate() - 1);

        // Check if the current date is a weekday (not a weekend)
        if (isWeekend(currentDateCopy)) {
          if (last7WeekdaysCount < 7) {
            last7Weekdays.push({
              start: startOfDay(currentDateCopy),
              end: endOfDay(currentDateCopy)
            });
            last7WeekdaysCount++;
          } else if (beforeLast7WeekdaysCount < 7) {
            beforeLast7Weekdays.push({
              start: startOfDay(currentDateCopy),
              end: endOfDay(currentDateCopy)
            });
            beforeLast7WeekdaysCount++;
          }
        }

        // Move to the previous day
        currentDate.setDate(currentDate.getDate() - 1);
      }

      console.log('updateRangeView', { last7Weekdays, beforeLast7Weekdays });

      const last7Week = last7Weekdays
        .sort((rowA, rowB) => Number(rowA.start) - Number(rowB.start))
        .map((dateItem, index) => {
          return {
            startDate: new Date(zoneToData(dateItem.start)),
            endDate: new Date(zoneToData(dateItem.end)),
            pastStartDate: new Date(zoneToData(dateItem.start)),
            pastEndDate: new Date(zoneToData(dateItem.end)),
            key: '',
            ...hourlyPayload
          };
        });

      const beforeLast7Week = beforeLast7Weekdays
        .sort((rowA, rowB) => Number(rowA.start) - Number(rowB.start))
        .map((dateItem, index) => {
          return {
            startDate: new Date(zoneToData(dateItem.start)),
            endDate: new Date(zoneToData(dateItem.end)),
            pastStartDate: new Date(zoneToData(dateItem.start)),
            pastEndDate: new Date(zoneToData(dateItem.end)),
            key: '',
            ...hourlyPayload
          };
        });

      if (beforeLast7Week && last7Week) {
        const rangesSelected = beforeLast7Week.concat(last7Week);

        console.log('RANGES', rangesSelected);
        // doCompare ? rangesSelected : last7Week
        setSelectedDateRange(last7Week);

        dateInfo = {
          startDate: last7Weekdays.sort((rowA, rowB) => Number(rowA.start) - Number(rowB.start))[0].start,
          endDate: last7Weekdays.sort((rowA, rowB) => Number(rowA.start) - Number(rowB.start))[last7Weekdays.length - 1].end,
          pastStartDate: beforeLast7Weekdays.sort((rowA, rowB) => Number(rowA.start) - Number(rowB.start))[0].start,
          pastEndDate: beforeLast7Weekdays.sort((rowA, rowB) => Number(rowA.start) - Number(rowB.start))[beforeLast7Weekdays.length - 1]
            .end,
          dates: last7Week.map((row) => row.startDate),
          compareDates: beforeLast7Week.map((row) => row.startDate),
          key: 'Last 7 Weekends',
          current: last7Week,
          previous: beforeLast7Week,
          ...hourlyPayload
        };

        setTempDateRange(dateInfo);
      }
    } else if (rangeInfo.key === 'Last 7 Same day' && from == 1) {
      // Get the day of the week (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
      const currentDayOfWeek = getDay(currentDate);

      // Initialize an array to store the date ranges for the last 7 Mondays from the last 7 weeks
      const last7SameDays = [];

      // Calculate the date ranges for the last 7 Mondays from the last 7 weeks
      for (let i = 0; i < 8; i++) {
        const targetDay = subWeeks(currentDate, i); // Subtract weeks for the same day of the week

        // Check if the day matches Monday
        if (getDay(targetDay) === currentDayOfWeek && !isSameDay(targetDay, currentDate)) {
          const targetDate = eachDayOfInterval({ start: targetDay, end: targetDay });
          last7SameDays.push({ start: targetDate[0], end: targetDate[0] });
        }
      }

      // Initialize an array to store the date ranges for the 7 Mondays before the last 7 Mondays
      const beforeLast7SameDays = [];

      // Calculate the date ranges for the 7 Mondays before the last 7 Mondays
      for (let i = 8; i < 15; i++) {
        const targetDay = subWeeks(currentDate, i); // Subtract weeks for the same day of the week

        // Check if the day matches Monday
        if (getDay(targetDay) === currentDayOfWeek && !isSameDay(targetDay, currentDate)) {
          const targetDate = eachDayOfInterval({ start: targetDay, end: targetDay });
          beforeLast7SameDays.push({ start: targetDate[0], end: targetDate[0] });
        }
      }

      if (last7SameDays && beforeLast7SameDays) {
        console.log({ last7SameDays, beforeLast7SameDays });

        const last7Week = last7SameDays
          .sort((rowA, rowB) => Number(rowA.start) - Number(rowB.start))
          .map((dateItem, index) => {
            return {
              startDate: dateItem.start,
              endDate: dateItem.end,
              pastStartDate: dateItem.start,
              pastEndDate: dateItem.end,
              key: 'Last 7 Same day',
              ...hourlyPayload
            };
          });

        const beforeLast7Week = beforeLast7SameDays
          .sort((rowA, rowB) => Number(rowA.start) - Number(rowB.start))
          .map((dateItem, index) => {
            return {
              startDate: dateItem.start,
              endDate: dateItem.end,
              pastStartDate: dateItem.start,
              pastEndDate: dateItem.end,
              key: 'Last 7 Same day',
              ...hourlyPayload
            };
          });

        if (beforeLast7Week && last7Week) {
          const rangesSelected = beforeLast7Week.concat(last7Week);

          console.log('RANGES', rangesSelected);
          // doCompare ? rangesSelected : last7Week
          setSelectedDateRange(last7Week);

          dateInfo = {
            startDate: last7SameDays.sort((rowA, rowB) => Number(rowA.start) - Number(rowB.start))[0].start,
            endDate: last7SameDays.sort((rowA, rowB) => Number(rowA.end) - Number(rowB.end))[last7SameDays.length - 1].end,
            pastStartDate: beforeLast7SameDays.sort((rowA, rowB) => Number(rowA.start) - Number(rowB.start))[0].start,
            pastEndDate: beforeLast7SameDays.sort((rowA, rowB) => Number(rowA.end) - Number(rowB.end))[beforeLast7SameDays.length - 1].end,
            dates: last7Week.map((row) => row.startDate),
            ...hourlyPayload,
            compareDates: beforeLast7Week.map((row) => row.startDate),
            key: 'Last 7 Same day',
            current: last7Week,
            previous: beforeLast7Week
          };

          setTempDateRange(dateInfo);

          console.log('RANGES', rangesSelected);

          // doCompare ? rangesSelected : last7Week
          setSelectedDateRange(last7Week);
        }
      }

      console.log({ last7SameDays, beforeLast7SameDays });
    } else if (rangeInfo.key === 'Lifetime' && from == 1 && commonState?.dateInfo?.start_date && isDate(new Date(commonState?.dateInfo?.start_date)) && commonState?.dateInfo?.end_date && isDate(new Date(commonState?.dateInfo?.end_date))) {
      const getDaysAvailable = differenceInCalendarDays(
        new Date(commonState?.dateInfo?.end_date),
        new Date(commonState?.dateInfo?.start_date)
      );
      dateInfo = {
        startDate: new Date(zoneToData(new Date(commonState?.dateInfo?.start_date))),
        endDate: new Date(zoneToData(new Date(commonState?.dateInfo?.end_date))),
        pastStartDate: new Date(zoneToData(subDays(new Date(commonState?.dateInfo?.start_date), getDaysAvailable))),
        pastEndDate: new Date(zoneToData(subDays(new Date(commonState?.dateInfo?.start_date), 1))),
        key: 'Lifetime',
        ...hourlyPayload
      };
      setDoCompare(0);
      console.log('dateInfo', { getDaysAvailable, dateInfo });
      setTempDateRange(dateInfo);
      setDoCompare(0);
      setSelectedDateRange([{ ...dateInfo, key: '' }]);
    } else if (rangeInfo.key === 'Custom') {
      // For SQP, use ISO week snapping and calculate comparison based on selected duration
      const snapped = snapToIsoWeeks(rangeInfo.startDate, rangeInfo.endDate);
      const comparison = calculateComparisonPeriod(snapped.weekStart, snapped.weekEnd);
      
      dateInfo = {
        startDate: snapped.weekStart,
        endDate: snapped.weekEnd,
        pastStartDate: comparison.pastStartDate,
        pastEndDate: comparison.pastEndDate,
        key: 'Custom',
        ...hourlyPayload
      };

      console.log('dateInfo', { dateInfo });

      setTempDateRange(dateInfo);

      setSelectedDateRange([{ ...dateInfo, key: '' }]);
    } else if (rangeInfo.key === 'Last 60 Days' && from == 1) {
      const currentDateInZone = new Date(zoneToData(new Date()));

      const startDate = subDays(currentDateInZone, 60);
      const endDate = subDays(currentDateInZone, 1);
      const pastStartDate = subDays(currentDateInZone, 120);
      const pastEndDate = subDays(currentDateInZone, 61);
      dateInfo = {
        startDate: new Date(startDate),
        endDate: new Date(endDate),
        pastStartDate: new Date(pastStartDate),
        pastEndDate: new Date(pastEndDate),
        key: 'Last 60 Days',
        ...hourlyPayload
      };
      setTempDateRange(dateInfo);
      setSelectedDateRange([{ ...dateInfo, key: '' }]);
    } else if (rangeInfo.key === 'Last 90 Days' && from == 1) {
      const currentDateInZone = new Date(zoneToData(new Date()));

      const startDate = subDays(currentDateInZone, 90);
      const endDate = subDays(currentDateInZone, 1);
      const pastStartDate = subDays(currentDateInZone, 180);
      const pastEndDate = subDays(currentDateInZone, 91);
      dateInfo = {
        startDate: new Date(startDate),
        endDate: new Date(endDate),
        pastStartDate: new Date(pastStartDate),
        pastEndDate: new Date(pastEndDate),
        key: 'Last 90 Days',
        ...hourlyPayload
      };
      setTempDateRange(dateInfo);
      setSelectedDateRange([{ ...dateInfo, key: '' }]);
    }

    // updateFocus()


  }, [hoursList, commonState?.dateInfo, audienceCustomDays, customWeeks]);

  useEffect(() => {
    setDateSelected(preDateSelected)
  }, [preDateSelected])

  const saveDateRange = useCallback(() => {
    if (tempDateRange) {
      setShowdatepicker(false);
      doCompareSelected(doCompare);
      // setDateSelected(tempDateRange);
      onEmitDateRange(tempDateRange, doCompare, '')
      setDisableButton(true);
      setHoursList(doUpdateDayOrHourList)
    }
  }, [tempDateRange, doCompare]);

  // useEffect(() => {
  //   const getDateRanges = ['Last 7 Days', 'Last 7 Months', 'Last 7 Weekdays', 'Last 7 Weekends'];

  //   let getDateRangeResponse = [];

  //   const getRanges = async () => {
  //     await getDateRanges.forEach(async (range) => {
  //       if (range === 'Last 7 Days') {
  //         const response = await getlast7WeeksRange();
  //         getDateRangeResponse.push(response);
  //       } else if (range === 'Last 7 Months') {
  //         const response = await getlast7MonthsRange();
  //         getDateRangeResponse.push(response);
  //       } else if (range === 'Last 7 Weekdays') {
  //         const response = await getLast7WeekDaysRange();
  //         getDateRangeResponse.push(response);
  //       } else if (range === 'Last 7 Weekends') {
  //         const response = await getlast7MonthsRange();
  //         getDateRangeResponse.push(response);
  //       }
  //       if (getDateRanges.length == getDateRangeResponse.length) {
  //         setDateRanges(getDateRangeResponse);
  //       }
  //     });
  //   };

  //   getRanges();

  //   return () => {
  //     getDateRangeResponse = [];
  //   };
  // }, []);

  // useEffect(() => {
  //   onEmitDateRange(dateSelected, compareSelected, '');
  // }, [dateSelected, compareSelected]);

  const { smartSearchState } = useSmartSearchContext();
  let isSmartSearchEnabled =
    smartSearchState?.filter_object?.dateInfo && Object.keys(smartSearchState?.filter_object?.dateInfo).length > 0 ? true : false;
  let defaultDateInfo = isSmartSearchEnabled ? smartSearchState?.filter_object?.dateInfo : dateSelected;

  const location = useLocation();
  const validPaths = ['/amazon-dsp/campaign', '/amazon-dsp/adgroup', '/amazon-dsp/audience', '/amazon-dsp/creative'];

  const amzDspPage = validPaths.includes(location.pathname)&& amzDspState?.activeTab !== 3;

  const { dayPartingState } = useDayPartingContext();
  const isDayScheduleApplied = dayPartingState?.dateInfo && Object.keys(dayPartingState?.dateInfo).length > 0 ? true : false;
  defaultDateInfo = isDayScheduleApplied ? dayPartingState?.dateInfo : defaultDateInfo;

  const [open, setOpen] = useState(false);

  const calendarRef = useRef(null);

   useEffect(() => {
    return () => {
      setShowdatepicker(false);
    };
  }, [location]);

   // Handle outside click
   const handleOutsideClick = (event) => {
    const calendarElement = document.querySelector('.calender_style');
    if (calendarElement && !calendarElement.contains(event.target)) {
      setOpen(false);
      setShowdatepicker(false);
    }
  };
  
  // useEffect(() => {
  //   const rootElement = document.querySelector('#root'); 
  //   if (!rootElement) return;

  //   if (showDatepicker) {
  //     rootElement.classList.add('calendar-overlay');
  //     rootElement.addEventListener('click', handleOutsideClick);
  //   } else {
  //     rootElement.classList.remove('calendar-overlay');
  //     rootElement.removeEventListener('click', handleOutsideClick);
  //   }

  //   return () => {
  //     document.removeEventListener('click', handleOutsideClick);
  //     rootElement.classList.remove('calendar-overlay');
  //   };
  // }, [showDatepicker]);

  // Don't render until initialized to prevent crashes
  if (!isInitialized) {
    return <CalendarSkeletonLoader />;
  }

  if (isCalendarOnly === 1 && defaultCalendar == 1 && defaultDateInfo?.startDate && isDate(new Date(defaultDateInfo?.startDate))) {
    return (
      <>
        <Whisper preventOverflow placement={placement ? placement : 'bottomEnd'} speaker={DatePicker} open={showDatepicker} trigger="none">
          <div 
            className={`bg-white p-3 text-dark d-flex align-items-center f-w-500 f-12 cursor-pointer rounded-8 calender_style ${isSmartSearchEnabled || isDayScheduleApplied ? 'disabled' : amzDspPage ? 'disabled' : ''
              }`}
            onClick={(e) => {
              e.stopPropagation(); // Prevent event bubbling
              if (isSmartSearchEnabled) return false;
              setOpen(true); 
              setSelectedDateRange(
                preDateSelected && Object.keys(preDateSelected).length > 0
                  ? [preDateSelected]
                  : [
                    {
                      startDate: subDays(new Date(), 30),
                      endDate: subDays(new Date(), 1),
                      pastStartDate: subDays(new Date(), 60),
                      pastEndDate: subDays(new Date(), 31),
                      key: 'Last 30 Days'
                    }
                  ]
              );
              setTempDateRange(
                preDateSelected && Object.keys(preDateSelected).length > 0
                  ? {
                    ...preDateSelected,
                    dates: [],
                    compareDates: []
                  }
                  : {
                    startDate: subDays(new Date(), 30),
                    endDate: subDays(new Date(), 1),
                    pastStartDate: subDays(new Date(), 60),
                    pastEndDate: subDays(new Date(), 31),
                    dates: [],
                    compareDates: [],
                    key: 'Last 30 Days'
                  }
              );
              setDoCompare(
                showCompareInfo && payloadState.dateRange && Object.keys(payloadState.dateRange).length > 0 && 'compare' in payloadState.dateRange
                  ? payloadState.dateRange?.compare
                  : false
              );
              setDisableButton(true);
              setShowdatepicker(true);
            }}
          >
            <div className='d-flex align-items-center'>
              <img src={Calender} alt="date_picker" className="wid-20 mr-2" />
              <div className="d-flex flex-column gap-4 l-15">
                <div className="d-flex align-items-center">
                  <p className="f-w-600 mr-1">
                    {defaultDateInfo?.key != '' && defaultDateInfo?.key != 'selection' ? defaultDateInfo?.key : 'Custom'} :{' '}
                  </p>
                    {defaultDateInfo?.startDate && isDate(new Date(defaultDateInfo.startDate)) ? format(new Date(defaultDateInfo.startDate), 'dd MMM, yyyy') : (
                      <SkeletonLoader.Text width="100px" height="12px" classNames="d-inline-block" />
                    )}{' '}
                  {defaultDateInfo?.startDate && defaultDateInfo?.endDate && isDate(new Date(defaultDateInfo.startDate)) && isDate(new Date(defaultDateInfo.endDate)) && format(new Date(defaultDateInfo.startDate), 'dd MMM, yyyy') !== format(new Date(defaultDateInfo.endDate), 'dd MMM, yyyy')
                    ? ` - ${format(new Date(defaultDateInfo.endDate), 'dd MMM, yyyy')}`
                    : ``}
                </div>
                {showCompareInfo && defaultDateInfo?.pastStartDate && (doCompare || defaultDateInfo?.compare == 1 || isDayScheduleApplied ) ? (
                  <div className="f-10 f-w-400">
                    Compared to&nbsp;:&nbsp;{defaultDateInfo?.pastStartDate && isDate(new Date(defaultDateInfo.pastStartDate)) ? format(new Date(defaultDateInfo.pastStartDate), 'dd MMM, yyyy') : 'Loading...'}
                    {defaultDateInfo?.pastStartDate && defaultDateInfo?.pastEndDate && isDate(new Date(defaultDateInfo.pastStartDate)) && isDate(new Date(defaultDateInfo.pastEndDate)) && format(new Date(defaultDateInfo.pastStartDate), 'dd MMM, yyyy') !== format(new Date(defaultDateInfo.pastEndDate), 'dd MMM, yyyy')
                      ? ' - ' + format(new Date(defaultDateInfo.pastEndDate), 'dd MMM, yyyy')
                      : ''}
                  </div>
                ) : null}
              </div>
            </div>
            <CollapseArrowIcon />
          </div>
        </Whisper>
      </>
    );
  }

  if (isCustomCalender === 1) {
    return (
      <Whisper preventOverflow placement="auto" speaker={DatePicker} open={showDatepicker} trigger="none">
        <div onClick={(e) => {
          e.stopPropagation(); // Prevent event bubbling
          setShowdatepicker(true);
        }}>{children}</div>
      </Whisper>
    );
  }

  if (masterSearchCalendar == 1) {
    return (
      <>
        {/* CALENDAR MODAL */}
        <div className={`date-picker-modal-one theme_date_pcker z-2300 ${selectedDateRange.length > 1 ? 'hide_range' : ''} bg-white`}>
          <section className="px-2 border-bottom pb-2">
            <DateRangePicker
              // moveRangeOnFirstSelection={true}
              // retainEndDateOnFirstSelection={true}
              editableDateInputs={true}
              calendarFocus={isThisMonth(tempDateRange.startDate) == true ? 'backwards' : 'forwards'}
              locale={enGB}
              ranges={[...selectedDateRange].concat([
                ...(doCompare == 1 && showCompareInfo && selectedDateRange?.length > 0
                  ? [
                    {
                      startDate: dateFormat(selectedDateRange[0].pastStartDate),
                      endDate: dateFormat(selectedDateRange[0].pastEndDate),
                      color: '#ACACAC'
                    }
                  ]
                  : [])
              ])}
              months={isDirectType === 'between' ? 2 : 1}
              // shownDate={new Date(lastMonth)}
              // initialFocusedRange={{
              //   startDate: subMonths(new Date(), 1),
              //   endDate: new Date(),
              //   key:'Default'
              // }}
              shouldDisableDate={date => isAfter(date, new Date())}
              onChange={(item) => {
                console.log('CHANGE', item);
                if (item.range1) {
                  console.log('item.range1', item);
                  const snapped = snapToIsoWeeks(item.range1.startDate, item.range1.endDate);
                  const comparison = calculateComparisonPeriod(snapped.weekStart, snapped.weekEnd);
                  onEmitDateRange({
                    startDate: snapped.weekStart,
                    endDate: snapped.weekEnd,
                    pastStartDate: comparison.pastStartDate,
                    pastEndDate: comparison.pastEndDate,
                    key: 'Custom'
                  });
                } else if (item.selection) {
                  const snapped = snapToIsoWeeks(item.selection.startDate, item.selection.endDate || item.selection.startDate);
                  const comparison = calculateComparisonPeriod(snapped.weekStart, snapped.weekEnd);
                  onEmitDateRange({
                    startDate: snapped.weekStart,
                    endDate: snapped.weekEnd,
                    pastStartDate: comparison.pastStartDate,
                    pastEndDate: comparison.pastEndDate,
                    key: 'Custom'
                  });
                } else if (!item.range) {
                  const dateInfo = { ...Object.values(item)[0], key: 'Custom' };
                  const snapped = snapToIsoWeeks(dateInfo.startDate, dateInfo.endDate || dateInfo.startDate);
                  const comparison = calculateComparisonPeriod(snapped.weekStart, snapped.weekEnd);
                  onEmitDateRange({
                    startDate: snapped.weekStart,
                    endDate: snapped.weekEnd,
                    pastStartDate: comparison.pastStartDate,
                    pastEndDate: comparison.pastEndDate,
                    key: 'Custom'
                  });
                }
              }}
              direction="horizontal"
              maxDate={maxDate !== undefined ? maxDate : getLastCompleteSaturday()}
              staticRanges={[]}
            />
          </section>
        </div>
        {/* CALENDAR MODAL */}
      </>
    );
  }

  return (
    <div>
      {/* <h2 className="f-24 f-w-700 text-dark">{reportTitle}</h2> */}
      <section className="position-relative">
        <section className={`cal_tar d-flex align-items-center position-cng ${classes}`}>
          {defaultCalendar == 1 ? (
            <Whisper
              preventOverflow
              placement={placement ? placement : 'bottomEnd'}
              speaker={DatePicker}
              open={showDatepicker}
              trigger="none"
            >
              <div
                className={`bg-white p-3 text-dark d-flex align-items-center f-w-500 f-15 cursor-pointer rounded-8 calender_style ${isSmartSearchEnabled ? 'disabled' : ''
                  }`}
                onClick={(e) => {
                  e.stopPropagation(); // Prevent event bubbling
                  if (isSmartSearchEnabled) return false;
                  setDisableButton(true);
                  setShowdatepicker(true);
                }}
              >
                <img src={Calender} alt="date_picker" className="wid-20 mr-2" />
                <div className="d-flex flex-column gap-10">
                  <div className="d-flex align-items-center">
                    <p className="f-w-600 mr-1">
                      {dateSelected?.key != '' && dateSelected?.key != 'selection' ? dateSelected?.key : 'Custom'} :{' '}
                    </p>
                    {dateSelected?.startDate && isDate(new Date(dateSelected.startDate)) ? format(new Date(dateSelected.startDate), 'dd MMM, yyyy') : (
                      <SkeletonLoader.Text width="100px" height="12px" classNames="d-inline-block" />
                    )}{' '}
                    {dateSelected?.startDate && dateSelected?.endDate && isDate(new Date(dateSelected.startDate)) && isDate(new Date(dateSelected.endDate)) && format(new Date(dateSelected.startDate), 'dd MMM, yyyy') !== format(new Date(dateSelected.endDate), 'dd MMM, yyyy')
                      ? ` - ${format(new Date(dateSelected.endDate), 'dd MMM, yyyy')}`
                      : ``}
                  </div>
                  {/* {compare ? <div className='f-10 f-w-400'>Compared&nbsp;:&nbsp; Jan 07 - Jan 13, 2024</div> : null} */}
                </div>
                <CollapseArrowIcon />
              </div>
            </Whisper>
          ) : null}
          {/* This handles the overlay click */}
          {showDatepicker && <div className="position-fixed inset-0 z-1500" onClick={(e) => {
            e.stopPropagation(); // Prevent event bubbling
            setShowdatepicker(false);
          }}></div>}
          {/* {showDatepicker && (
            
          )} */}
        </section>
      </section>
    </div>
  );
}


export const HourlyFilterData = ({ hoursList, setHoursList, doUpdateDayOrHourList }) => {
  const [open, setOpen] = useState(false);
  const [selectAll, setSelectAll] = useState(hoursList.every((item) => item.checked == 1) == true ? true : false);
  const [isModified, setIsModified] = useState(false);

  const handleCheckBoxChange = (event, time) => {
    const updatedList = hoursList.map(item =>
      item.value === time.value ? { ...item, checked: event.target.checked ? 1 : 0 } : item
    );
    setHoursList(updatedList);
    setSelectAll(updatedList.every(item => item.checked === 1));
    setIsModified(true);
  };

  const handleSelectAllChange = (event) => {
    const isChecked = event.target.checked;
    setSelectAll(isChecked);
    setHoursList(hoursList.map(item => ({ ...item, checked: isChecked ? 1 : 0 })));
    setIsModified(true);
  };

  const handleSubmit = () => {
    if (isModified && (selectAll || hoursList.some(item => item.checked === 1))) {
      const checkedHours = hoursList.filter(item => item.checked === 1);
      console.log('Selected Hours:', checkedHours);
      doUpdateDayOrHourList(hoursList);
      setIsModified(false);
      setOpen(false);
    }
  };

  const handleCancel = () => {
    // setHoursList(hoursList.map(item => ({ ...item, checked: 1 })));
    // setSelectAll(true);
    // setIsModified(false);
    setOpen(false);
  };

  const popover = (
    <Popover style={{ zIndex: 10001, borderRadius: '8px' }}>
      <div className="HourlyFilterData-dropdown px-3 py-1">
        <div className="py-2">
          <p className="f-16 f-w-600">Select Hours</p>
        </div>
        <CheckBox
          label="Select All"
          checked={selectAll || hoursList.some((item) => item.checked == 1)}
          classes={`${hoursList.every((item) => item.checked == 1) == false && hoursList.some((item) => item.checked == 1) ? 'indet_check' : '' }`}
          action={handleSelectAllChange}
        />
        <div className="d-flex gap-10 align-items-center py-2">
          <div>
            <ul>
              {hoursList
                .slice(0,
                  (hoursList.length / 2)).map(time => (
                    <li key={time.value}>
                      <CheckBox
                        action={(event) => handleCheckBoxChange(event, time)}
                        checked={time.checked === 1}
                        label={time.label}
                        classes="f-10 mb-0 HourlyFilterData-checkBox"
                      />
                    </li>
                  ))}
            </ul>
          </div>
          <div>
            <ul>
              {hoursList.slice(hoursList.length / 2, hoursList.length).map(time => (
                <li key={time.value}>
                  <CheckBox
                    action={(event) => handleCheckBoxChange(event, time)}
                    checked={time.checked === 1}
                    label={time.label}
                    classes="f-10 mb-0 HourlyFilterData-checkBox"
                  />
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="d-flex align-items-center justify-content-end py-2">
          <p
            className="cls-btn cursor-pointer rounded-4 f-10 py-10 px-3 f-w-600 mr-3 f-202226"
            onClick={(e) => {
              e.stopPropagation(); // Prevent event bubbling
              handleCancel();
            }}
          >
            Cancel
          </p>
          <p
            className={`rounded-4 f-10 py-10 px-3 f-w-600 bg-blue  text-white mt-0 ${isModified && (selectAll || hoursList.some(item => item.checked === 1)) ? 'cursor-pointer' : 'disabled'
              }`}
            onClick={(e) => {
              e.stopPropagation(); // Prevent event bubbling
              handleSubmit();
            }}
          >
            Apply
          </p>
        </div>
      </div>
    </Popover>
  );

  return (
    <Whisper
      placement="top"
      trigger="click"
      open={open}
      onClose={() => setOpen(false)}
      onClick={() => setOpen(!open)}
      speaker={popover}
    >
      <button className="f-12 f-w-400 d-flex align-items-center w-100 py-1 px-2">
        <p>Select Hours {hoursList.every((item) => item.checked == 1) == true ? 'All' :`(${hoursList.filter(item => item.checked === 1).length})`} &nbsp; {open ? <UpChevronIcon style={{ fontSize: '10px' }} /> : <DownChevron width={10} height={10} />}</p>
      </button>
    </Whisper>
  );
};

export const hourFilterList = [
  { label: '12 AM - 1 AM', value: '00:00:00', checked: 1 },
  { label: '01 AM - 2 AM', value: '01:00:00', checked: 1 },
  { label: '02 AM - 3 AM', value: '02:00:00', checked: 1 },
  { label: '03 AM - 4 AM', value: '03:00:00', checked: 1 },
  { label: '04 AM - 5 AM', value: '04:00:00', checked: 1 },
  { label: '05 AM - 6 AM', value: '05:00:00', checked: 1 },
  { label: '06 AM - 7 AM', value: '06:00:00', checked: 1 },
  { label: '07 AM - 8 AM', value: '07:00:00', checked: 1 },
  { label: '08 AM - 9 AM', value: '08:00:00', checked: 1 },
  { label: '09 AM - 10 AM', value: '09:00:00', checked: 1 },
  { label: '10 AM - 11 AM', value: '10:00:00', checked: 1 },
  { label: '11 AM - 12 PM', value: '11:00:00', checked: 1 },
  { label: '12 PM - 1 PM', value: '12:00:00', checked: 1 },
  { label: '01 PM - 2 PM', value: '13:00:00', checked: 1 },
  { label: '02 PM - 3 PM', value: '14:00:00', checked: 1 },
  { label: '03 PM - 4 PM', value: '15:00:00', checked: 1 },
  { label: '04 PM - 5 PM', value: '16:00:00', checked: 1 },
  { label: '05 PM - 6 PM', value: '17:00:00', checked: 1 },
  { label: '06 PM - 7 PM', value: '18:00:00', checked: 1 },
  { label: '07 PM - 8 PM', value: '19:00:00', checked: 1 },
  { label: '08 PM - 9 PM', value: '20:00:00', checked: 1 },
  { label: '09 PM - 10 PM', value: '21:00:00', checked: 1 },
  { label: '10 PM - 11 PM', value: '22:00:00', checked: 1 },
  { label: '11 PM - 12 PM', value: '23:00:00', checked: 1 },
];

export const TimeBadgeClock = ({ defaultDateInfo }) => {
  
  const [theme] = useContext(ThemeContext);
  const location = useLocation()
  const doFetchTime = useApi(services.postService)
  const [marketingStreamHour, setMarketingStreamHour] = useState(null)
  const { payloadDispatch } = usePayloadContext()
  const showHourDisplay = useMemo(() => {
    return location.pathname.includes('amazon-snapshot') || location.pathname.includes('amazon-master-overview')
  }, [location.pathname])

  useEffect(() => {
    if(showHourDisplay){
        payloadDispatch({
          type: 'MARKETING_STREAM_HOUR_LOADING',
          payload: true
        })
      services.postService({
        url: location.pathname.includes('amazon-snapshot') ? '/snapshots/getLastMarketingStreamHour' : '/masteroverview/getLastMarketingStreamHour',
        payload: {
          start_date: dateFormatPayload(defaultDateInfo?.startDate),
        }
      }).then(res => {
        if (res) {
          setMarketingStreamHour(res?.data?.result?.time)
          payloadDispatch({
            type: 'UPDATE_MARKETING_STREAM_HOUR',
            payload: res?.data?.result?.time
          })
          payloadDispatch({
            type: 'UPDATE_MARKETING_STREAM_HOUR',
            payload: res?.data?.result?.time
          })
        }
      }).catch(err => {
        payloadDispatch({
          type: 'MARKETING_STREAM_HOUR_LOADING',
          payload: false
        })
      }).finally(() => {
        payloadDispatch({
          type: 'MARKETING_STREAM_HOUR_LOADING',
          payload: false
        })
      })
    }
  }, [showHourDisplay])


  if(showHourDisplay && marketingStreamHour) return (
    <CustomTooltip title={`Till this hour ( ${formatTimeWithAbbr(marketingStreamHour)} ) data is being updated through the marketing stream services, \n also it's compared by the same hour data of the comparing period`} placement={'bottom'}>
    <div className='nav_datepicker d-none d-lg-inline-block   data-time-badge-container'>
      <div className={`bg-white p-3 text-dark d-flex align-items-center f-w-500 f-12 cursor-pointer rounded-8 calender_style  date-time-badge ${theme.theme === 'DARK' ? 'dark' : 'light'}`}>
        <Clock className="clock-icon" />
        <p>
          {formatTimeWithAbbr(marketingStreamHour)}
        </p>
      </div>
    </div>
    </CustomTooltip>
  )

  return null
}

const formatTimeWithAbbr = (timeString) => {
  const [hours] = timeString.split(':');
  const hour = parseInt(hours, 10);
  const period = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour % 12 || 12;
  return `${displayHour}:00 ${period}`;
};


export const Clock = ({ className }) => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g clip-path="url(#clip0_1_2)">
    <path d="M7 3.5V7.25L9.25 9M7 13C7.78793 13 8.56815 12.8448 9.2961 12.5433C10.0241 12.2417 10.6855 11.7998 11.2426 11.2426C11.7998 10.6855 12.2417 10.0241 12.5433 9.2961C12.8448 8.56815 13 7.78793 13 7C13 6.21207 12.8448 5.43185 12.5433 4.7039C12.2417 3.97595 11.7998 3.31451 11.2426 2.75736C10.6855 2.20021 10.0241 1.75825 9.2961 1.45672C8.56815 1.15519 7.78793 1 7 1C5.4087 1 3.88258 1.63214 2.75736 2.75736C1.63214 3.88258 1 5.4087 1 7C1 8.5913 1.63214 10.1174 2.75736 11.2426C3.88258 12.3679 5.4087 13 7 13Z" stroke="#8F8F91" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
    </g>
    <defs>
    <clipPath id="clip0_1_2">
    <rect width="14" height="14" fill="white"/>
    </clipPath>
    </defs>
    </svg>
);